import cx from 'classnames';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationProps {
  /** Current page, 0-indexed. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  onChange: (page: number) => void;
  className?: string;
  /**
   * What each page is called, indexed by page. Given years ("2026", "2025"), the pager reads as the thing
   * being paged through rather than as a count of pages. Falls back to the page number.
   */
  labels?: string[];
  /**
   * Lay the pages out right-to-left, so page 0 sits at the right-hand end. The year pages are newest-first,
   * but years read left-to-right the way numbers do, so 2025 belongs left of 2026.
   */
  reversed?: boolean;
}

const ELLIPSIS = 'ellipsis';

// Google-style page list: always show the first and last page, plus a small window around the current
// one, collapsing the gaps to a single ellipsis. Returns 0-indexed page numbers interleaved with
// `ELLIPSIS` markers.
const buildPages = (page: number, pageCount: number): (number | typeof ELLIPSIS)[] => {
  const pages = new Set<number>([0, pageCount - 1, page]);
  for (let offset = 1; offset <= 2; offset += 1) {
    if (page - offset >= 0) pages.add(page - offset);
    if (page + offset <= pageCount - 1) pages.add(page + offset);
  }
  const sorted = [...pages].sort((a, b) => a - b);

  const withGaps: (number | typeof ELLIPSIS)[] = [];
  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1] > 1) withGaps.push(ELLIPSIS);
    withGaps.push(value);
  });
  return withGaps;
};

/** Numbered pager (‹ Prev  1 2 … 9 10  Next ›) — like a search-results footer. */
export const Pagination = ({
  page,
  pageCount,
  onChange,
  className,
  labels,
  reversed,
}: PaginationProps): ReactElement | null => {
  const { t } = useTranslation();

  if (pageCount <= 1) return null;

  const arrow =
    'inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted-foreground';

  // Reversed, the button on the left still steps towards the label on the left — the higher index.
  const backTo = reversed ? page + 1 : page - 1;
  const forwardTo = reversed ? page - 1 : page + 1;
  const atBack = reversed ? page === pageCount - 1 : page === 0;
  const atForward = reversed ? page === 0 : page === pageCount - 1;
  const pages = buildPages(page, pageCount);

  // The pager sits at the foot of the list, so changing page otherwise leaves you at the bottom of one you
  // have not read. Jumped, not smooth: a year of sprints is a long way to scroll through.
  const goTo = (next: number): void => {
    onChange(next);
    window.scrollTo({ top: 0 });
  };

  return (
    <nav className={cx('flex items-center justify-center gap-1', className)} aria-label={t('pagination.label')}>
      <button type="button" className={cx(arrow, 'mr-2')} onClick={() => goTo(backTo)} disabled={atBack}>
        <ChevronLeft className="size-4" />
        {t('pagination.previous')}
      </button>

      {(reversed ? [...pages].reverse() : pages).map((item, index) =>
        item === ELLIPSIS ? (
          <span
            key={`ellipsis-${Number(index)}`}
            aria-hidden
            className="flex size-8 items-center justify-center text-sm text-muted-foreground"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => goTo(item)}
            aria-current={item === page ? 'page' : undefined}
            className={cx(
              'flex h-8 items-center justify-center rounded-md text-sm tabular-nums transition-colors',
              // A label such as "2026" needs room a page number doesn't.
              labels ? 'min-w-8 px-2' : 'w-8',
              item === page
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            {labels?.[item] ?? item + 1}
          </button>
        ),
      )}

      <button type="button" className={cx(arrow, 'ml-2')} onClick={() => goTo(forwardTo)} disabled={atForward}>
        {t('pagination.next')}
        <ChevronRight className="size-4" />
      </button>
    </nav>
  );
};

export default Pagination;
